<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipelines: {
    type: Array,
    default: () => [],
  },
  stageCounts: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'open-create-modal',
  'select-pipeline',
  'edit-pipeline',
  'delete-pipeline',
]);
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-surface-1 overflow-y-auto">
    <!-- Top Header -->
    <header
      class="sticky top-0 z-20 flex h-16 w-full items-center justify-between border-b border-n-weak bg-n-surface-1/90 backdrop-blur-md px-6 shadow-sm"
    >
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-bold text-n-slate-12">Funis</h1>
        <span
          class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-solid-2 px-1.5 text-xs font-bold text-n-slate-11 border border-n-weak"
        >
          {{ pipelines.length }}
        </span>
      </div>

      <button
        type="button"
        class="flex items-center gap-1.5 rounded-xl bg-n-brand px-4 py-2 text-xs font-bold text-[#1c1c1c] shadow hover:brightness-95 transition-all cursor-pointer"
        @click="emit('open-create-modal')"
      >
        <Icon icon="i-lucide-plus" class="size-4" />
        <span>Adicionar Funil</span>
      </button>
    </header>

    <!-- Funnels Cards Grid -->
    <main class="max-w-6xl w-full mx-auto p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="flex flex-col justify-between rounded-2xl border border-n-weak bg-n-solid-1/40 p-5 shadow-xs hover:border-n-brand transition-all group"
        >
          <div>
            <!-- Card Header -->
            <div class="flex items-start justify-between gap-3 mb-3">
              <div class="flex items-center gap-3">
                <div
                  class="flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand group-hover:bg-n-brand group-hover:text-[#1c1c1c] transition-colors"
                >
                  <Icon icon="i-lucide-columns-3" class="size-5" />
                </div>
                <div>
                  <h3
                    class="text-sm font-extrabold text-n-slate-12 tracking-tight"
                  >
                    {{ pipeline.name }}
                  </h3>
                  <span class="text-[11px] text-n-slate-10 font-mono">
                    ID: {{ pipeline.id }}
                  </span>
                </div>
              </div>

              <span
                class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-solid-3 px-2 text-xs font-bold text-n-slate-11 border border-n-weak"
              >
                {{ pipeline.stages ? pipeline.stages.length : 0 }} etapas
              </span>
            </div>

            <!-- Description -->
            <p
              class="text-xs text-n-slate-10 mb-4 line-clamp-2 leading-relaxed"
            >
              {{ pipeline.description || 'Sem descrição cadastrada.' }}
            </p>

            <!-- Stages Badges Preview -->
            <div class="flex flex-wrap gap-1.5 mb-6">
              <span
                v-for="stage in (pipeline.stages || []).slice(0, 5)"
                :key="stage.id"
                class="inline-flex items-center gap-1.5 rounded-lg bg-n-surface-1 border border-n-weak px-2 py-1 text-[11px] font-semibold text-n-slate-11"
              >
                <span
                  class="size-2 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color || '#3B82F6' }"
                />
                <span class="truncate max-w-[100px]">{{ stage.name }}</span>
              </span>
              <span
                v-if="pipeline.stages && pipeline.stages.length > 5"
                class="text-[11px] font-bold text-n-slate-10 self-center"
              >
                +{{ pipeline.stages.length - 5 }}
              </span>
            </div>
          </div>

          <!-- Card Actions Footer -->
          <div
            class="flex items-center justify-between pt-4 border-t border-n-weak"
          >
            <button
              type="button"
              class="flex items-center gap-1.5 text-xs font-bold text-n-slate-11 hover:text-n-brand transition-colors cursor-pointer"
              @click="emit('edit-pipeline', pipeline.id)"
            >
              <Icon icon="i-lucide-settings" class="size-3.5" />
              <span>Configurações</span>
            </button>

            <button
              type="button"
              class="flex items-center gap-1.5 rounded-xl bg-n-solid-2 hover:bg-n-brand hover:text-[#1c1c1c] border border-n-weak px-3.5 py-1.5 text-xs font-bold text-n-slate-12 transition-all cursor-pointer"
              @click="emit('select-pipeline', pipeline.id)"
            >
              <span>Ver Quadro</span>
              <Icon icon="i-lucide-arrow-right" class="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
